/**
 * 지점 허브 — "이 지역에서 검색"한 범위 안의 지점만(09-29).
 *
 * 허브 데이터셋은 범위와 무관한 카테고리 전체라, 예전엔 지도를 옮겨 "이 지역에서 검색"을
 * 눌러도 목록·핀이 전국 그대로였다(거리 기준점만 바뀌었다). 주소가 지도 영역 검색
 * (범위 + location_search=MAP_AREA)일 때 그 범위로 좁힌다. 도시·지역·국가 랜딩의 범위 지점은
 * lib/seo/hub-landing.hubScopeStationIds 가 고른다(10-15). 정렬(가까운 순)은 그대로
 * 목록 쪽이 맡는다. 범위 안에 없으면 빈 목록 — 전 지점으로 되돌리지 않는다.
 */
import type { ModelSummary, StationSummary } from "@/types/mobility";
import type { SearchBounds } from "@/lib/search/search-state";
import { calculateDistance } from "@/lib/api/products/geo";

type ParamSource = URLSearchParams | { get(key: string): string | null };

/** 지도 영역 검색 주소의 범위. 아니면(또는 숫자가 아니면) null. */
export function mapAreaBoundsOf(params: ParamSource): SearchBounds | null {
  if (params.get("location_search") !== "MAP_AREA") return null;
  const read = (k: string) => {
    const v = params.get(k);
    return v ? Number(v) : NaN;
  };
  const b = {
    ne_lat: read("ne_lat"),
    ne_lng: read("ne_lng"),
    sw_lat: read("sw_lat"),
    sw_lng: read("sw_lng"),
  };
  return Object.values(b).every(Number.isFinite) ? b : null;
}

export function isInBounds(point: { lat: number; lng: number }, b: SearchBounds): boolean {
  return (
    point.lat >= b.sw_lat &&
    point.lat <= b.ne_lat &&
    point.lng >= b.sw_lng &&
    point.lng <= b.ne_lng
  );
}

export interface HubStationSet {
  stations: StationSummary[];
  stationsById: Map<string, StationSummary>;
  models: ModelSummary[];
}

/**
 * 범위 안 지점으로 좁힌 지점·차종 집합. 범위가 없으면 입력을 그대로(참조 동일) 돌려준다.
 * 차종의 "지점 N곳"(stationIds)도 같은 집합으로 줄이고, 범위 안 지점이 하나도 없는 차종은
 * 뺀다 — 지점 목록·핀·카탈로그 개수가 같은 지점 집합을 본다.
 */
export function boundHubStations(
  set: HubStationSet,
  bounds: SearchBounds | null,
  /**
   * 도시·지역·국가 랜딩의 지점 id — 범위(지도 영역 검색)가 없을 때만 이 지점으로 좁힌다.
   * 지도를 옮겨 "이 지역에서 검색"하면 그 범위가 이긴다(도시·국가와 같은 규칙).
   */
  regionStationIds: ReadonlySet<string> | null = null,
): HubStationSet {
  if (!bounds && !regionStationIds) return set;
  const stations = set.stations.filter((s) =>
    bounds ? isInBounds(s, bounds) : regionStationIds!.has(s.id),
  );
  const stationsById = new Map(stations.map((s) => [s.id, s]));
  const models: ModelSummary[] = [];
  for (const model of set.models) {
    const stationIds = new Set([...model.stationIds].filter((id) => stationsById.has(id)));
    if (stationIds.size > 0) models.push({ ...model, stationIds });
  }
  return { stations, stationsById, models };
}

/** 지도 핀을 보여 주려고 맞출 때 함께 넣는 가까운 지점 수(09-29). */
export const REVEAL_NEAREST_COUNT = 5;

/**
 * 기준점(지금 지도 중심)에서 가까운 순으로 n 개 — 목록에는 지점이 있는데 지도 화면에는
 * 핀이 하나도 없을 때, "중심 + 이 점들"에 카메라를 맞춰 핀을 보여 준다. 모자라면 전부.
 * 거리가 같으면 입력 순서를 지킨다(정렬 안정).
 */
export function nearestPoints<T extends { lat: number; lng: number }>(
  origin: { lat: number; lng: number },
  points: readonly T[],
  n: number = REVEAL_NEAREST_COUNT,
): T[] {
  if (n <= 0) return [];
  return points
    .map((point, index) => ({
      point,
      index,
      km: calculateDistance(origin.lat, origin.lng, point.lat, point.lng),
    }))
    .sort((a, b) => a.km - b.km || a.index - b.index)
    .slice(0, n)
    .map((entry) => entry.point);
}
